import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { getActivePeriodIds } from './getActivePeriods.js';
import { GroupGenderType } from './GroupGenderType.js';
import { GroupPrivateSettings } from './GroupPrivateSettings.js';
import { GroupSettings, WaitingListType } from './GroupSettings.js';
import { GroupType } from './GroupType.js';
import { Gender } from './members/Gender.js';
import { PermissionLevel } from './PermissionLevel.js';
import { PermissionsResourceType } from './PermissionsResourceType.js';
import { StockReservation } from './StockReservation.js';
export var GroupStatus;
(function (GroupStatus) {
    GroupStatus["Open"] = "Open";
    GroupStatus["Closed"] = "Closed";
    /**
     * @deprecated
     */
    GroupStatus["Archived"] = "Archived";
})(GroupStatus || (GroupStatus = {}));
export function getGroupStatusName(status) {
    switch (status) {
        case GroupStatus.Open:
            return $t(`d265d3d1-ef01-4cec-8485-e95290ba40d5`);
        case GroupStatus.Closed:
            return $t(`ae8d3a27-6a56-4ae8-ada6-c843f01625b0`);
        case GroupStatus.Archived:
            return $t(`14cf0829-fa0a-4797-8deb-54c095f724ef`);
    }
}
export class Group extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.type = GroupType.Membership;
        this.organizationId = '';
        this.periodId = '';
        this.defaultAgeGroupId = null;
        this.waitingList = null;
        this.stockReservations = [];
        /**
         * @deprecated
         */
        this.cycle = 0;
        this.settings = GroupSettings.create({});
        this.createdAt = new Date();
        this.deletedAt = null;
        /**
         * Only set when you have access to this information
         */
        this.privateSettings = null;
        /**
         * Manually close a group
         */
        this.status = GroupStatus.Open;
        // Permission checking cache:
        this.event = null;
        // Permission checking cache (waiting list -> group -> event)
        this.parentGroup = null;
    }
    getDiffName() {
        return this.settings.name;
    }
    static defaultSort(a, b) {
        if (a.settings.maxAge && !b.settings.maxAge) {
            return -1;
        }
        if (b.settings.maxAge && !a.settings.maxAge) {
            return 1;
        }
        if (!b.settings.maxAge && !a.settings.maxAge) {
            // name
            return Group.nameSort(a, b);
        }
        if (a.settings.maxAge > b.settings.maxAge) {
            return 1;
        }
        if (a.settings.maxAge < b.settings.maxAge) {
            return -1;
        }
        return Group.nameSort(a, b);
    }
    static nameSort(a, b) {
        if (a.settings.name.toLowerCase() < b.settings.name.toLowerCase()) {
            return -1;
        }
        if (a.settings.name.toLowerCase() > b.settings.name.toLowerCase()) {
            return 1;
        }
        return 0;
    }
    getMemberCount({ waitingList } = {}) {
        return this.settings.getMemberCount({ waitingList });
    }
    /**
     * Return the pre registration date only if is is active right now
     */
    get activePreRegistrationDate() {
        if (!this.settings.registrationStartDate) {
            // Registration start date is a requirement for pre registrations
            return null;
        }
        if (this.settings.registrationStartDate < new Date() || this.settings.waitingListType !== WaitingListType.PreRegistrations) {
            // Start date is in the past: registrations are open
            return null;
        }
        return this.settings.preRegistrationsDate;
    }
    /**
     * Closed now, but will open in the future
     */
    get notYetOpen() {
        if (!this.settings.registrationStartDate) {
            return false;
        }
        const now = new Date();
        const preRegistrationDate = this.activePreRegistrationDate;
        if (this.settings.registrationStartDate > now && (!preRegistrationDate || preRegistrationDate > now)) {
            // Start date or pre registration date are in the future
            return true;
        }
        return false;
    }
    /**
     * No registrations and waiting list registrations are possible if closed
     */
    get closed() {
        if (this.status !== GroupStatus.Open) {
            return true;
        }
        if (this.notYetOpen) {
            // Start date or pre registration date are in the future
            return true;
        }
        const now = new Date();
        if (this.settings.registrationEndDate && this.settings.registrationEndDate < now) {
            return true;
        }
        return false;
    }
    /**
     * Returns all parent and grandparents of this group
     */
    getParentCategories(all, recursive = true) {
        const map = new Map();
        const parents = all.filter(g => g.groupIds.includes(this.id));
        for (const parent of parents) {
            map.set(parent.id, parent);
            if (recursive) {
                const hisParents = parent.getParentCategories(all);
                for (const pp of hisParents) {
                    map.set(pp.id, pp);
                }
            }
        }
        return [...map.values()];
    }
    hasAccess(permissions, allCategories, permissionLevel = PermissionLevel.Read) {
        if (!permissions) {
            return false;
        }
        if (permissions.hasResourceAccess(PermissionsResourceType.Groups, this.id, permissionLevel)) {
            return true;
        }
        // Check parent categories
        const parentCategories = this.getParentCategories(allCategories);
        for (const category of parentCategories) {
            if (permissions.hasResourceAccess(PermissionsResourceType.GroupCategories, category.id, permissionLevel)) {
                return true;
            }
        }
        return false;
    }
    isPublic(allCategories) {
        for (const parent of this.getParentCategories(allCategories)) {
            if (!parent.settings.public) {
                return false;
            }
        }
        return true;
    }
    hasReadAccess(permissions, allCategories) {
        return this.hasAccess(permissions, allCategories, PermissionLevel.Read);
    }
    hasWriteAccess(permissions, allCategories) {
        return this.hasAccess(permissions, allCategories, PermissionLevel.Write);
    }
    hasFullAccess(permissions, allCategories) {
        return this.hasAccess(permissions, allCategories, PermissionLevel.Full);
    }
    get squareImage() {
        var _a;
        return (_a = this.settings.squarePhoto) !== null && _a !== void 0 ? _a : this.settings.coverPhoto;
    }
    /**
     * Return the period ids that we consider as active for a given
     * group and organization combination.
     */
    getActivePeriodIds(organization) {
        return getActivePeriodIds(this.periodId, organization);
    }
    getRecommendedFilter(organization) {
        const filter = [];
        const periodIds = [...this.getActivePeriodIds(organization !== null && organization !== void 0 ? organization : null)];
        const periodIdFilter = periodIds.length === 1 ? { periodId: periodIds[0] } : { periodId: { $in: periodIds } };
        if (this.settings.minAge !== null) {
            filter.push({
                age: {
                    $gt: this.settings.minAge - 1,
                },
            });
        }
        if (this.settings.maxAge !== null) {
            filter.push({
                age: {
                    $lt: this.settings.maxAge + 1,
                },
            });
        }
        if (this.settings.genderType === GroupGenderType.OnlyMale) {
            filter.push({
                gender: Gender.Male,
            });
        }
        if (this.settings.genderType === GroupGenderType.OnlyFemale) {
            filter.push({
                gender: Gender.Male,
            });
        }
        if (this.settings.requireGroupIds.length) {
            filter.push({
                registrations: {
                    $elemMatch: {
                        groupId: {
                            $in: this.settings.requireGroupIds,
                        },
                    },
                },
            });
        }
        if (this.settings.preventGroupIds.length) {
            filter.push({
                registrations: {
                    $elemMatch: {
                        groupId: {
                            $not: {
                                $in: this.settings.preventGroupIds,
                            },
                        },
                    },
                },
            });
        }
        if (this.settings.requireDefaultAgeGroupIds.length) {
            filter.push({
                registrations: {
                    $elemMatch: {
                        $and: [
                            periodIdFilter,
                            {
                                group: {
                                    defaultAgeGroupId: {
                                        $in: this.settings.requireDefaultAgeGroupIds,
                                    },
                                },
                            },
                        ],
                    },
                },
            });
        }
        else {
            if (this.settings.requirePlatformMembershipOn !== null) {
                const requirePlatformMembershipOn = this.settings.requirePlatformMembershipOn;
                filter.push({
                    $or: [
                        {
                            platformMemberships: {
                                $elemMatch: {
                                    startDate: {
                                        $lte: requirePlatformMembershipOn,
                                    },
                                    endDate: {
                                        $gt: requirePlatformMembershipOn,
                                    },
                                },
                            },
                        }, {
                            platformMemberships: {
                                $elemMatch: {
                                    startDate: {
                                        $lte: { $: '$now' },
                                    },
                                    endDate: {
                                        $gt: { $: '$now' },
                                    },
                                },
                            },
                        },
                    ],
                });
            }
            else if (this.settings.requirePlatformMembershipOnRegistrationDate !== null) {
                filter.push({
                    platformMemberships: {
                        $elemMatch: {
                            startDate: {
                                $lte: { $: '$now' },
                            },
                            endDate: {
                                $gt: { $: '$now' },
                            },
                        },
                    },
                });
            }
        }
        if (organization && (!this.settings.requireOrganizationIds.length || this.settings.requireOrganizationIds.includes(organization.id))) {
            filter.push({
                registrations: {
                    $elemMatch: {
                        $and: [
                            periodIdFilter,
                            {
                                organizationId: organization.id,
                            },
                        ],
                    },
                },
            });
        }
        else if (this.settings.requireOrganizationIds.length) {
            filter.push({
                registrations: {
                    $elemMatch: {
                        $and: [
                            periodIdFilter,
                            {
                                organizationId: {
                                    $in: this.settings.requireOrganizationIds,
                                },
                            },
                        ],
                    },
                },
            });
        }
        if (this.settings.requireOrganizationTags.length) {
            filter.push({
                registrations: {
                    $elemMatch: {
                        $and: [
                            periodIdFilter,
                            {
                                organization: {
                                    tags: {
                                        $in: this.settings.requireOrganizationTags,
                                    },
                                },
                            },
                        ],
                    },
                },
            });
        }
        return filter;
    }
    static decode(...args) {
        const result = super.decode.call(this, ...args);
        // Create circular reference for permission checking in the frontend
        if (result.waitingList && result.type === GroupType.EventRegistration) {
            result.waitingList.parentGroup = result;
        }
        return result;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Group.prototype, "id", void 0);
__decorate([
    field({ decoder: new EnumDecoder(GroupType), version: 286 })
], Group.prototype, "type", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 250 })
], Group.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 265 })
], Group.prototype, "periodId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 267 })
], Group.prototype, "defaultAgeGroupId", void 0);
__decorate([
    field({ decoder: Group, nullable: true, version: 292 })
], Group.prototype, "waitingList", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StockReservation), version: 298 })
], Group.prototype, "stockReservations", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], Group.prototype, "cycle", void 0);
__decorate([
    field({ decoder: GroupSettings })
], Group.prototype, "settings", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 187 })
], Group.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 187 })
], Group.prototype, "deletedAt", void 0);
__decorate([
    field({ decoder: GroupPrivateSettings, nullable: true, version: 10 })
], Group.prototype, "privateSettings", void 0);
__decorate([
    field({ decoder: new EnumDecoder(GroupStatus), version: 192 })
], Group.prototype, "status", void 0);
//# sourceMappingURL=Group.js.map